import { computed, defineComponent, ref, toRef, watch } from '@vue/composition-api';
import { validationMixin } from 'vuelidate';
import { minValue, numeric, required } from 'vuelidate/lib/validators';
import { useNamespacedState } from 'vuex-composition-helpers';
import { apolloSdk } from '@/api/apollo';
import { useAddPropertyOnlineMutation, useUpdatePropertyOnlineMutation } from '@/api/apollo/generated/use';
import { useListableProperties } from '@/api/legacy/useListableProperties';
import { ACCOUNT } from '@/store/types/namespaces';
import { throwFallible, useForm } from './utilities';
const units = [
    { label: 'Seconds', value: 's', multiplier: 1 },
    { label: 'Minutes', value: 'm', multiplier: 60 },
    { label: 'Hours', value: 'h', multiplier: 60 * 60 },
    { label: 'Days', value: 'd', multiplier: 60 * 60 * 24 },
];
function secondsToUnit(seconds) {
    let unit = units[0].value;
    let value = 1;
    for (let i = units.length - 1; i >= 0; i -= 1) {
        const remainder = seconds / units[i].multiplier;
        if (remainder % 1 === 0) {
            unit = units[i].value;
            value = remainder;
            break;
        }
    }
    return { unit, value };
}
function unitValueToSeconds(unit, value) {
    const def = units.find((u) => u.value === unit);
    return value * (def ?? { multiplier: 1 }).multiplier;
}
export function getCreateName(watchRef, untilValue, untilUnit) {
    let name = '$online/';
    if (watchRef && watchRef !== '$ts') {
        name += `${watchRef}SeenInLast`;
    }
    else {
        name += 'seenInLast';
    }
    return `${name}${untilValue}${untilUnit}`;
}
export default defineComponent({
    name: 'OnlinePropertyEditor',
    mixins: [validationMixin],
    props: {
        editingId: { type: String, default: null },
    },
    emits: ['close', 'save'],
    validations: {
        form: {
            alias: {},
            watchRef: {},
            untilValue: { required, numeric, minValue: minValue(1) },
            untilUnit: { required },
        },
    },
    setup(props, context) {
        const editingId = toRef(props, 'editingId');
        const propertyLoading = ref(false);
        const editingProperty = ref(null);
        const titleText = computed(() => (editingProperty.value ? 'Edit online property' : 'Create online property'));
        const errorMessage = ref(null);
        const { form, setForm, invalid } = useForm({
            alias: null,
            watchRef: null,
            untilUnit: units[1].value,
            untilValue: 5,
        });
        const showAdvanced = ref(false);
        const isOpen = ref(false);
        const open = () => { isOpen.value = true; };
        const close = (field) => {
            errorMessage.value = null;
            setForm();
            isOpen.value = false;
            if (field)
                context.emit('save', field);
            context.emit('close');
        };
        const { id: accountId } = useNamespacedState(ACCOUNT, ['id']);
        const { listableProperties: legacyProperties } = useListableProperties();
        const { mutate: createProperty, loading: addPropertyLoading, } = useAddPropertyOnlineMutation({
            refetchQueries: ['Properties'], awaitRefetchQueries: true,
        });
        const { mutate: updateProperty, loading: updatePropertyLoading, } = useUpdatePropertyOnlineMutation({
            refetchQueries: ['Properties'], awaitRefetchQueries: true,
        });
        const mutateLoading = computed(() => addPropertyLoading.value || updatePropertyLoading.value);
        async function save() {
            if (invalid())
                return;
            const { alias, watchRef: watchRefObj, untilUnit, untilValue, } = form.value;
            let watchRef = '$ts';
            if (watchRefObj)
                watchRef = watchRefObj.name === '$ts' ? '$ts' : `$ts/${watchRefObj.name}`;
            let action;
            let field;
            if (editingProperty.value && editingId.value) {
                // updating
                field = editingProperty.value.field;
                const input = {
                    alias: alias ?? field.replace(/^\$online\//, ''),
                    watchRef,
                    untilSeconds: unitValueToSeconds(untilUnit, untilValue),
                    hidden: editingProperty.value.hidden,
                    version: editingProperty.value.version,
                };
                const nodeId = editingId.value;
                action = async () => {
                    const result = await updateProperty({ nodeId, input });
                    throwFallible(result?.errors, result?.data?.updatePropertyOnline.errors);
                };
            }
            else {
                // creating
                field = getCreateName(watchRef, untilValue, untilUnit);
                const input = {
                    alias: alias ?? field.replace(/^\$online\//, ''),
                    field,
                    watchRef,
                    untilSeconds: unitValueToSeconds(untilUnit, untilValue),
                    hidden: false,
                };
                action = async () => {
                    const result = await createProperty({ accountId: accountId.value, input });
                    throwFallible(result?.errors, result?.data?.addPropertyOnline.errors, { NodeExistsError: `A ${watchRef} seen in last ${untilValue}${untilUnit} already exists` });
                };
            }
            try {
                await action();
                close(field);
            }
            catch (e) {
                errorMessage.value = e?.message || 'Unable to save';
            }
        }
        watch(editingId, async (nodeId) => {
            editingProperty.value = null;
            errorMessage.value = null;
            setForm();
            if (nodeId === null)
                return;
            propertyLoading.value = true;
            try {
                const result = await apolloSdk.Property({ nodeId });
                if (result?.node?.__typename !== 'PropertyOnline')
                    throw new TypeError('Property is not an online property');
                const { unit: untilUnit, value: untilValue } = secondsToUnit(result.node.untilSeconds);
                const field = result.node.watchRef.replace(/^\$ts\//, '');
                const watchRefObj = legacyProperties.value.find((p) => p.name === field);
                if (!watchRefObj)
                    throw new TypeError(`Watched property ${field} was not found`);
                setForm({
                    alias: result.node.alias,
                    watchRef: watchRefObj,
                    untilUnit,
                    untilValue,
                });
                editingProperty.value = result.node;
            }
            catch (e) {
                errorMessage.value = e?.message || `Unable to load ${nodeId}`;
            }
            propertyLoading.value = false;
        });
        return {
            isOpen,
            open,
            close,
            titleText,
            errorMessage,
            form,
            units,
            showAdvanced,
            propertyLoading,
            mutateLoading,
            save,
        };
    },
});
